import { customAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const defaultUrlPattern = () =>
  "https://gitlab\\.com/.*|https://[^/]*\\.gitlab\\.com/.*";

const KEYWORDS =
  "repositories merge requests pipelines ci cd devsecops issues source control self hosted";

export const gitlab = defineProduct({
  name: "GitLab",
  tagLine: "DevSecOps and CI/CD",
  logo,
  tags: ["engineering"],
  variants: [
    productVariant({
      identifier: "gitlab",
      authLabel: "Personal access token",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern,
      auth: customAuth("gitlab"),
      steps: [
        {
          title: "Enter your GitLab instance URL",
          items: [
            {
              kind: "url",
              label: "GitLab instance URL",
              required: true,
              placeholder: "https://gitlab.com",
              defaultValue: "https://gitlab.com",
              target: { kind: "configuration", field: "instanceUrl" },
              helperText:
                "Use https://gitlab.com for GitLab.com or your self-hosted URL.",
            },
          ],
        },
        {
          title: "Create a personal access token",
          items: [
            {
              kind: "markdown",
              content: ({ instanceUrl }: Record<string, string>) => {
                const tokenLink = instanceUrl
                  ? `[create a personal access token](${instanceUrl}/-/user_settings/personal_access_tokens?name=3B&scopes=api)`
                  : "go to **User Settings > Access Tokens** and create a personal access token";
                return `In GitLab, ${tokenLink}. The link pre-selects the \`api\` scope, which grants full read and write access to the GitLab API. To narrow that to a single project or group, create a project or group access token with the \`api\` scope instead of a personal one. Copy the token and paste it below.`;
              },
            },
            {
              kind: "input",
              label: "Personal access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) => `${instanceUrl}/api/v4/user`,
      },
      publicEnvVars: ({ instanceUrl }) => ({
        GITLAB_URL: instanceUrl || "",
      }),
    }),
    productVariant({
      identifier: "gitlabOAuth",
      authLabel: "OAuth application",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern,
      auth: customAuth("gitlab"),
      steps: [
        {
          title: "Enter your GitLab instance URL",
          items: [
            {
              kind: "url",
              label: "GitLab instance URL",
              required: true,
              placeholder: "https://gitlab.com",
              defaultValue: "https://gitlab.com",
              target: { kind: "configuration", field: "instanceUrl" },
              helperText:
                "Use https://gitlab.com for GitLab.com or your self-hosted URL.",
            },
          ],
        },
        {
          title: "Create an OAuth application in GitLab",
          items: [
            {
              kind: "markdown",
              content: ({ instanceUrl }: Record<string, string>) => {
                const settingsLink = instanceUrl
                  ? `[**Settings > Applications**](${instanceUrl}/-/user_settings/applications)`
                  : "**Settings > Applications**";
                return `In GitLab, open ${settingsLink} (under your user, a group, or the admin area) and select **Add new application**. Give it a name, paste the redirect URI below, and keep **Confidential** enabled.`;
              },
            },
            {
              kind: "copyable",
              label: "Redirect URI",
              value: "{API_URL}/connected-apps/oauth/gitlabOAuth/callback",
            },
            {
              kind: "markdown",
              content:
                "Before saving, check the `api` [scope](https://docs.gitlab.com/integration/oauth_provider/#authorized-applications) in the same form; 3B requests it when authorizing to read and write the GitLab API on your behalf. GitLab shows the application ID and secret only after you save.",
            },
          ],
        },
        {
          title: "Enter your application credentials",
          items: [
            {
              kind: "input",
              label: "Application ID",
              placeholder: "Paste your Application ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Secret",
              placeholder: "Paste your Secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
        {
          title: "Authorize your account in GitLab",
          items: [
            {
              kind: "markdown",
              content:
                "Finally, we’ll authorize your GitLab account so 3B can sync repositories on your behalf.",
            },
            {
              kind: "oauth",
              dependsOn: [
                { kind: "configuration", field: "instanceUrl" },
                { kind: "oauthConnection", field: "clientId" },
                { kind: "oauthConnection", field: "clientSecret" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: ({ instanceUrl }) =>
          `${instanceUrl}/oauth/authorize`,
        tokenEndpoint: ({ instanceUrl }) => `${instanceUrl}/oauth/token`,
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["api"],
      },
      connectionTest: {
        protocol: "http",
        request: ({ instanceUrl }) => `${instanceUrl}/api/v4/user`,
      },
      publicEnvVars: ({ instanceUrl }) => ({
        GITLAB_URL: instanceUrl || "",
      }),
    }),
  ],
});
